if (!customElements.get('ml-custom-size-request')) {
  customElements.define(
    'ml-custom-size-request',
    class MlCustomSizeRequest extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;

        this.dialog = this.querySelector('[data-ml-custom-request-dialog]');
        this.openButton = this.querySelector('[data-ml-custom-request-open]');
        this.closeButtons = this.querySelectorAll('[data-ml-custom-request-close]');
        this.form = this.querySelector('form');
        this.items = this.querySelector('[data-ml-custom-request-items]');
        this.itemTemplate = this.querySelector('[data-ml-custom-request-item-template]');
        this.addButton = this.querySelector('[data-ml-custom-request-add]');
        this.submitButton = this.querySelector('[data-ml-custom-request-submit]');
        this.totalPanel = this.querySelector('[data-ml-custom-request-total-panel]');
        this.totalCount = this.querySelector('[data-ml-custom-request-total-count]');
        this.totalPrice = this.querySelector('[data-ml-custom-request-total]');
        this.hiddenCount = this.querySelector('[data-ml-custom-request-hidden-count]');
        this.hiddenTotal = this.querySelector('[data-ml-custom-request-hidden-total]');
        this.hiddenSizes = this.querySelector('[data-ml-custom-request-hidden-sizes]');
        this.variants = this.parseVariants();

        if (!this.dialog || !this.openButton) return;

        this.openButton.addEventListener('click', () => this.open());

        this.closeButtons.forEach((button) => {
          button.addEventListener('click', () => this.close());
        });

        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
          event.stopPropagation();
        });

        ['input', 'change'].forEach((eventName) => {
          this.dialog.addEventListener(eventName, (event) => {
            event.stopPropagation();
          });
        });

        this.dialog.addEventListener('close', () => {
          document.documentElement.classList.remove('ml-custom-request-open');
          this.openButton.focus({ preventScroll: true });
        });

        this.items?.addEventListener('input', (event) => {
          if (
            event.target.matches('[data-ml-custom-request-width]') ||
            event.target.matches('[data-ml-custom-request-length]')
          ) {
            this.updateAllQuotes();
          }
        });

        this.items?.addEventListener(
          'wheel',
          (event) => {
            if (
              event.target.matches('input[type="number"]') &&
              document.activeElement === event.target
            ) {
              event.target.blur();
            }
          },
          { passive: true }
        );

        this.items?.addEventListener('click', (event) => {
          const removeButton = event.target.closest('[data-ml-custom-request-remove]');
          if (!removeButton) return;

          const item = removeButton.closest('[data-ml-custom-request-item]');
          if (!item || this.getItems().length === 1) return;

          const nextFocus = item.previousElementSibling || item.nextElementSibling;
          item.remove();
          this.renumberItems();
          this.updateAllQuotes();
          nextFocus?.querySelector('[data-ml-custom-request-width]')?.focus();
        });

        this.addButton?.addEventListener('click', () => this.addItem());

        this.form?.addEventListener('submit', (event) => {
          if (!this.updateAllQuotes(true)) {
            event.preventDefault();
            this.getFirstInvalidInput()?.focus();
          }
        });

        this.renumberItems();
        this.updateAllQuotes();
        this.dataset.initialized = 'true';

        if (
          this.querySelector('[data-ml-custom-request-success]') ||
          this.querySelector('[data-ml-custom-request-errors]')
        ) {
          this.open();
        }
      }

      open() {
        if (typeof this.dialog.showModal === 'function') {
          if (!this.dialog.open) this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
        }

        document.documentElement.classList.add('ml-custom-request-open');

        requestAnimationFrame(() => {
          const success = this.querySelector('[data-ml-custom-request-success]');
          const firstInput = this.querySelector('[data-ml-custom-request-width]');
          (success || firstInput || this.dialog).focus({ preventScroll: true });
        });
      }

      close() {
        if (typeof this.dialog.close === 'function' && this.dialog.open) {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
        }

        document.documentElement.classList.remove('ml-custom-request-open');
        this.openButton.focus({ preventScroll: true });
      }

      getItems() {
        return Array.from(
          this.items?.querySelectorAll('[data-ml-custom-request-item]') || []
        );
      }

      addItem() {
        const source = this.itemTemplate?.content?.firstElementChild;
        if (!source || !this.items) return;

        const item = source.cloneNode(true);
        this.items.appendChild(item);
        this.renumberItems();
        this.updateAllQuotes();
        item.querySelector('[data-ml-custom-request-width]')?.focus();
        item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      renumberItems() {
        const items = this.getItems();

        items.forEach((item, index) => {
          const number = index + 1;
          const label = item.querySelector('[data-ml-custom-request-item-label]');
          const width = item.querySelector('[data-ml-custom-request-width]');
          const length = item.querySelector('[data-ml-custom-request-length]');
          const remove = item.querySelector('[data-ml-custom-request-remove]');

          if (label) label.textContent = `Rug ${number}`;
          if (width) width.setAttribute('aria-label', `Rug ${number} width in inches`);
          if (length) length.setAttribute('aria-label', `Rug ${number} length in inches`);
          if (remove) remove.hidden = items.length === 1;
        });
      }

      parseVariants() {
        const data = this.querySelector('[data-ml-custom-request-variants]');
        if (!data) return [];

        try {
          return JSON.parse(data.textContent);
        } catch (error) {
          return [];
        }
      }

      parseSize(value) {
        const match = String(value || '').match(
          /(\d+)\s*(?:"|″)?\s*[x×]\s*(\d+)/i
        );

        if (!match) return null;

        const first = Number(match[1]);
        const second = Number(match[2]);

        return {
          width: Math.min(first, second),
          length: Math.max(first, second)
        };
      }

      sizeKey(width, length) {
        return `${Math.min(width, length)}x${Math.max(width, length)}`;
      }

      findStandardVariant(width, length) {
        const key = this.sizeKey(width, length);

        return this.variants.find((variant) => {
          if (!variant.available) return false;

          const rawSize = String(variant.size || '').trim().toLowerCase();
          if (rawSize === 'custom size' || rawSize.includes('sample')) return false;

          const parsed = this.parseSize(variant.size);
          return parsed && this.sizeKey(parsed.width, parsed.length) === key;
        });
      }

      validationMessage(width, length) {
        if (!Number.isInteger(width) || !Number.isInteger(length)) {
          return 'Enter both dimensions in whole inches.';
        }

        if (width < 12 || width > 120) {
          return 'Width must be between 12 and 120 inches.';
        }

        if (length < 18) {
          return 'Length must be at least 18 inches.';
        }

        if (!Number.isSafeInteger(width * length)) {
          return 'Please enter a shorter length.';
        }

        return '';
      }

      calculateCents(width, length) {
        const rate = Number(this.dataset.rateCents || 1225);
        const currencyRate = Number(window.Shopify?.currency?.rate) || 1;
        return Math.round((width * length * rate * currencyRate) / 144);
      }

      formatMoney(cents) {
        const currency = this.dataset.currencyCode || 'USD';
        const locale = document.documentElement.lang || 'en-US';

        try {
          return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency
          }).format(cents / 100);
        } catch (error) {
          return `$${(cents / 100).toFixed(2)}`;
        }
      }

      updateAllQuotes(showErrors = false) {
        const items = this.getItems();
        const details = [];
        let totalCents = 0;
        let allValid = items.length > 0;

        items.forEach((item, index) => {
          const widthInput = item.querySelector('[data-ml-custom-request-width]');
          const lengthInput = item.querySelector('[data-ml-custom-request-length]');
          const widthRaw = widthInput?.value.trim() || '';
          const lengthRaw = lengthInput?.value.trim() || '';
          const width = Number(widthRaw);
          const length = Number(lengthRaw);

          if (!widthRaw || !lengthRaw) {
            allValid = false;
            this.hideItemQuote(item);
            if (showErrors) {
              this.showItemError(item, 'Enter both dimensions for this rug.');
            } else {
              this.clearItemError(item);
            }
            return;
          }

          const message = this.validationMessage(width, length);
          if (message) {
            allValid = false;
            this.hideItemQuote(item);
            this.showItemError(item, message);
            return;
          }

          this.clearItemError(item);

          const standardVariant = this.findStandardVariant(width, length);
          const area = (width * length) / 144;
          const cents = standardVariant
            ? Number(standardVariant.price)
            : this.calculateCents(width, length);
          const formattedPrice = this.formatMoney(cents);
          const note = standardVariant
            ? 'Exact standard size · standard price'
            : `${area.toFixed(2)} sq. ft. at $12.25 per sq. ft.`;

          totalCents += cents;
          details.push(
            `Rug ${index + 1}: ${width} × ${length} inches — ${note} — ${formattedPrice}`
          );
          this.showItemQuote(item, `${width} × ${length} in`, note, formattedPrice);
        });

        if (allValid) {
          const formattedTotal = this.formatMoney(totalCents);
          const rugLabel = items.length === 1 ? '1 rug' : `${items.length} rugs`;

          if (this.totalCount) this.totalCount.textContent = rugLabel;
          if (this.totalPrice) this.totalPrice.textContent = formattedTotal;
          if (this.totalPanel) this.totalPanel.hidden = false;
          if (this.hiddenCount) this.hiddenCount.value = String(items.length);
          if (this.hiddenTotal) this.hiddenTotal.value = formattedTotal;
          if (this.hiddenSizes) this.hiddenSizes.value = details.join('\n');
          if (this.submitButton) this.submitButton.disabled = false;
          return true;
        }

        if (this.totalPanel) this.totalPanel.hidden = true;
        if (this.hiddenCount) this.hiddenCount.value = '';
        if (this.hiddenTotal) this.hiddenTotal.value = '';
        if (this.hiddenSizes) this.hiddenSizes.value = '';
        if (this.submitButton) this.submitButton.disabled = true;
        return false;
      }

      showItemQuote(item, summaryText, noteText, priceText) {
        const quote = item.querySelector('[data-ml-custom-request-item-quote]');
        const summary = item.querySelector('[data-ml-custom-request-item-summary]');
        const note = item.querySelector('[data-ml-custom-request-item-note]');
        const price = item.querySelector('[data-ml-custom-request-item-price]');

        if (summary) summary.textContent = summaryText;
        if (note) note.textContent = noteText;
        if (price) price.textContent = priceText;
        if (quote) quote.hidden = false;
      }

      hideItemQuote(item) {
        const quote = item.querySelector('[data-ml-custom-request-item-quote]');
        if (quote) quote.hidden = true;
      }

      showItemError(item, message) {
        const error = item.querySelector('[data-ml-custom-request-dimension-error]');
        const inputs = item.querySelectorAll(
          '[data-ml-custom-request-width], [data-ml-custom-request-length]'
        );

        if (error) {
          error.textContent = message;
          error.hidden = false;
        }
        inputs.forEach((input) => input.setAttribute('aria-invalid', 'true'));
      }

      clearItemError(item) {
        const error = item.querySelector('[data-ml-custom-request-dimension-error]');
        const inputs = item.querySelectorAll(
          '[data-ml-custom-request-width], [data-ml-custom-request-length]'
        );

        if (error) {
          error.textContent = '';
          error.hidden = true;
        }
        inputs.forEach((input) => input.removeAttribute('aria-invalid'));
      }

      getFirstInvalidInput() {
        for (const item of this.getItems()) {
          const width = item.querySelector('[data-ml-custom-request-width]');
          const length = item.querySelector('[data-ml-custom-request-length]');
          const widthNumber = Number(width?.value);
          const lengthNumber = Number(length?.value);

          if (!width?.value.trim()) return width;
          if (!length?.value.trim()) return length;
          if (!Number.isInteger(widthNumber) || widthNumber < 12 || widthNumber > 120) {
            return width;
          }
          if (!Number.isInteger(lengthNumber) || lengthNumber < 18) return length;
        }

        return null;
      }
    }
  );
}